<template>
	<fieldset>
		<!-- Add heading -->
		<DsText
			v-if="state.currentQuestion.questionText"
			text-type="heading-2xl"
			text-style="medium"
			html-element="legend"
		>
			<span class="ds-hc-group__legend-text">{{
				state.currentQuestion.questionText
			}}</span>
		</DsText>

		<!-- Add description -->
		<DsText
			v-if="state.currentQuestion.subText"
			html-element="div"
			class="date-choice__description"
		>
			{{ state.currentQuestion.subText }}
		</DsText>
		<form class="date-choice__form" novalidate>
			<div
				v-for="(option, index) in options"
				:key="option.answerBlockId"
				class="date-choice__item"
			>
				<DsText
					v-if="option.fieldName"
					:id="`${option.answerBlockId}-label`"
					htmlElement="label"
					text-type="body-sm"
					text-style="regular"
					:for="option.answerBlockId"
					class="date-choice__label"
				>
					<span v-html="option.fieldName"></span>
				</DsText>
				<DsText
					v-if="option.helpText"
					:id="`${option.answerBlockId}-hint`"
					htmlElement="span"
					text-type="body-sm"
					text-style="regular"
					class="date-choice__hint"
				>
					{{ option.helpText }}
				</DsText>
				<input
					:type="option.clientSideInputType"
					:name="option.fieldName"
					:id="option.answerBlockId"
					:placeholder="option.placeholderText"
					:max="option.maxValue"
					:min="option.minValue"
					:required="option.required"
					:aria-required="option.required ? 'true' : 'false'"
					:aria-invalid="
						option.touched && option.errorMessage ? 'true' : 'false'
					"
					:aria-labelledby="`${option.answerBlockId}-label`"
					:aria-describedby="getDescribedBy(option)"
					:data-index="index"
					autocomplete="off"
					v-model="option.value"
					:inputmode="
						option.clientSideInputType === 'number'
							? 'numeric'
							: undefined
					"
					@input="deleteErrorMessage(option)"
					@blur="checkFieldValidity($event, option)"
					class="date-choice__input"
				/>
				<DsText
					v-if="option.touched && option.errorMessage"
					:id="`${option.answerBlockId}-error`"
					htmlElement="span"
					text-type="body-sm"
					text-style="regular"
					class="date-choice__error"
					role="alert"
					aria-live="polite"
				>
					<span v-html="option.errorMessage"></span>
				</DsText>
			</div>
		</form>
	</fieldset>
</template>

<script setup>
import { DsText } from '@coloplast/design-system';
import { ref, watch } from 'vue';
import { useHealthCheckState } from '../useHealthCheckState';

const { state } = useHealthCheckState();
const emit = defineEmits(['setAnswer']);
const answers = ref(
	state.answersByQuestionId[state.currentQuestion.questionBlockId] ?? [],
);
const options = ref([]);

createLocalOptions();

watch(
	() => state.currentQuestion.isTouched,
	() => {
		options.value.forEach((option) => {
			option.touched = true;
			const el = document.getElementById(option.answerBlockId);
			setValidationErrorMessage(el, option);
		});
	},
);

function createLocalOptions() {
	options.value = (state.currentQuestion?.options || []).map((o) => {
		return {
			...o,
			value:
				o.value ??
				answers.value?.find(
					(answer) => answer.answerBlockId === o.answerBlockId,
				)?.value ??
				'',
			errorMessage: '',
			touched: false,
		};
	});
}

function setAnswer() {
	emit('setAnswer', {
		answer: answers.value,
		questionBlockId: state.currentQuestion.questionBlockId,
	});
}

function customErrorMessages(item) {
	return {
		valueMissing: item.requiredValidationErrorMessage,
		rangeOverflow: item.rangeValidationErrorMessage,
		rangeUnderflow: item.rangeValidationErrorMessage,
	};
}

function getCustomMessage(validity, item) {
	const messages = customErrorMessages(item);

	for (const key in messages) {
		if (validity[key]) {
			return messages[key];
		}
	}

	return '';
}

function deleteErrorMessage(item) {
	item.errorMessage = '';

	if (item.touched) {
		const el = document.getElementById(item.answerBlockId);
		if (el) {
			setValidationErrorMessage(el, item);
		}
	}
}

function setValidationErrorMessage(elem, item) {
	if (elem.validity.valid) {
		item.errorMessage = '';
		return;
	}

	item.errorMessage =
		getCustomMessage(elem.validity, item) || elem.validationMessage;
}

function checkFieldValidity(e, item) {
	item.touched = true;
	const el = e.target;

	setValidationErrorMessage(el, item);

	if(!el.validity.valid) {
		state.currentQuestion.isValid = false;
		emit('setAnswer', { answer: [], notCollect: true });
		return;
	}

	const index = answers.value?.findIndex(
		(answer) => answer.answerBlockId === item.answerBlockId,
	);

	if (index === -1) {
		answers.value.push({
			answerBlockId: item.answerBlockId,
			value: item.value,
			weight: 1,
		});
	} else {
		answers.value[index].value = item.value;
	}

	const hasInvalidFields = options.value.some((option) => option.touched && option.errorMessage);
	if (!hasInvalidFields) {
		setAnswer();
	}
}

function getDescribedBy(option) {
	const ids = [];

	if (option.helpText) {
		ids.push(`${option.answerBlockId}-hint`);
	}

	if (option.touched && option.errorMessage) {
		ids.push(`${option.answerBlockId}-error`);
	}

	return ids.length ? ids.join(' ') : undefined;
}
</script>

<style scoped lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.date-choice {
	&__description {
		margin-block-start: ds-space(6);
	}

	&__form {
		display: grid;
		grid-template-columns: 1fr;
		inline-size: 100%;
		margin-block-start: ds-space(9);
		row-gap: ds-space(7);
	}

	&__item {
		display: flex;
		flex-direction: column;
		row-gap: ds-space(3);

		&:has(.date-choice__error) .date-choice__input:not(:focus) {
			border-color: ds-color('border', 'danger');
		}
	}

	&__label {
		color: ds-color('fg', 'muted');
		line-height: ds-space(7);
	}

	&__input {
		inline-size: 100%;
		padding: 10px ds-space(5);
		border-radius: 8px;
		border: 1px solid ds-color('border', 'strong');
		font-family: ds-font-family('heading');
		font-size: t-rem(16px);
		line-height: t-rem(24px);

		&::placeholder,
		&:invalid {
			color: ds-color('fg', 'placeholder');
		}

		&:focus {
			border: 1px solid ds-color('border', 'interactive');
			outline: 4px solid ds-color('focus', 'default');
			outline-offset: 2px;
		}
	}

	&__error {
		color: ds-color('border', 'danger');
	}
}
</style>
